// useStore.js
import { create } from 'zustand';
import {
  SupportRequestSocketItem,
  SupportRequestSocketRequest,
  SupportRequestSocketResponse,
} from '../Pending/Flipper-SupportRequestsPending/SupportRequestSocket';
import {
  ConfigForData,
  FormGroupItem,
  SelectedValueItem,
} from '@droplet-tech-code/core-common-types';
import { BookingClientWithPurchasesAndTickets } from '@naus-code/naus-admin-types';
import { onlyUniqueByFunc } from '~/utils/utils.helper';
import { dayjs } from '@droplet-tech-code/core-elements/module/utils/utils.date';

interface SupportRequestFocus {
  unassignedBookings: boolean;
}
interface KoalaQuoteFormData {
  currency: string;
  passengers: {
    id: string;
    firstName: string;
    lastName: string;
    nationality: string | undefined;
    dateOfBirth: string | undefined;
  }[];
  tripDetails: {
    id: string;
    origin: string;
    destination: string;
    companyId: string;
    departureDate: string;
    departureTime: string;
    arrivalDate: string;
    arrivalTime: string;
    vesselId: string;
  }[];
}

export const useCreateKoalaQuoteStore = create<{
  koalaQuoteForm: ConfigForData | undefined;
  initialiseKoalaQuoteForm: (
    booking: BookingClientWithPurchasesAndTickets,
    portList: SelectedValueItem[],
    vessels: SelectedValueItem[],
    companies: SelectedValueItem[],
  ) => void;
  addPassengerToForm: () => void;
}>((set) => ({
  koalaQuoteForm: undefined,
  initialiseKoalaQuoteForm: (
    booking: BookingClientWithPurchasesAndTickets,
    portList: SelectedValueItem[],
    vessels: SelectedValueItem[],
    companies: SelectedValueItem[],
  ) => {
    set((state) => {
      const entries = mapBookingToFormEntries(booking);
      return {
        ...state,
        koalaQuoteForm: [
          {
            items: [
              {
                id: 'valueCcy-booking',
                type: 'number',
                label: 'Value ' + entries.currency,
              },
            ],
          },
          {
            type: 'separator',
          },
          //Passengers
          ...entries.passengers.map((passenger): FormGroupItem => {
            const { id } = passenger;
            return {
              items: [
                {
                  id: 'firstName' + '-passenger-' + id,
                  type: 'text',
                  initialValue: passenger.firstName,
                  label: 'First Name',
                },
                {
                  id: 'lastName' + '-passenger-' + id,
                  type: 'text',
                  initialValue: passenger.lastName,
                  label: 'Last Name',
                },
                {
                  id: 'nationality' + '-passenger-' + id,
                  type: 'text',
                  initialValue: passenger.nationality,
                  label: 'Nationality',
                },
                {
                  id: 'dateOfBirth' + '-passenger-' + id,
                  type: 'date',
                  format: 'YYYY-MM-DD',
                  initialValue: passenger.dateOfBirth,
                  label: 'Date of birth',
                },
                {
                  id: 'include' + '-passenger-' + id,
                  type: 'toggle',
                  label: 'Include Passenger',
                  initialValue: true,
                },
              ],
            };
          }),
          {
            type: 'separator',
          },
          //Trips
          ...entries.tripDetails.map((trip): FormGroupItem => {
            const { id } = trip;
            return {
              items: [
                {
                  id: 'originId' + '-trip-' + id,
                  type: 'single-select',
                  label: 'Origin',
                  list: portList,
                  initialValue: trip.origin,
                },
                {
                  id: 'destinationId' + '-trip-' + id,
                  type: 'single-select',
                  label: 'Destination',
                  list: portList,
                  initialValue: trip.destination,
                },
                {
                  id: 'departureDate' + '-trip-' + id,
                  type: 'date',
                  format: 'YYYY-MM-DD',
                  initialValue: trip.departureDate,
                  label: 'Departure Date',
                },
                {
                  id: 'departureTime' + '-trip-' + id,
                  type: 'date',
                  format: 'HH:mm',
                  initialValue: trip.departureTime,
                  label: 'Departure Time',
                },
                {
                  id: 'arrivalDate' + '-trip-' + id,
                  type: 'date',
                  format: 'YYYY-MM-DD',
                  initialValue: trip.arrivalDate,
                  label: 'Arrival Date',
                },
                {
                  id: 'arrivalTime' + '-trip-' + id,
                  type: 'date',
                  format: 'HH:mm',
                  initialValue: trip.arrivalTime,
                  label: 'Arrival Time',
                },
                {
                  id: 'vesselId' + '-trip-' + id,
                  type: 'single-select',
                  label: 'Vessel',
                  list: vessels,
                  initialValue: trip.vesselId,
                },
                {
                  id: 'companyId' + '-trip-' + id,
                  type: 'single-select',
                  label: 'Company',
                  list: companies,
                  initialValue: trip.companyId,
                },
              ],
            };
          }),
        ],
      };
    });
  },
  addPassengerToForm: () => {
    set((state) => {
      if (!state.koalaQuoteForm) {
        return { ...state };
      }
      const currentForm = [...state.koalaQuoteForm];
      const firstIndex = currentForm.findIndex(
        (formItem) => formItem.type === 'separator',
      );
      const insertIndex = currentForm.findLastIndex(
        (formItem) => formItem.type === 'separator',
      );
      const newPassengerFormId = 'newPassenger' + (insertIndex - firstIndex);
      const newPassengerForm: FormGroupItem = {
        items: [
          {
            id: 'firstName' + '-passenger-' + newPassengerFormId,
            type: 'text',
            label: 'First Name',
          },
          {
            id: 'lastName' + '-passenger-' + newPassengerFormId,
            type: 'text',
            label: 'Last Name',
          },
          {
            id: 'nationality' + '-passenger-' + newPassengerFormId,
            type: 'text',
            label: 'Nationality',
          },
          {
            id: 'dateOfBirth' + '-passenger-' + newPassengerFormId,
            type: 'date',
            format: 'YYYY-MM-DD',
            label: 'Date of birth',
          },
          {
            id: 'include' + '-passenger-' + newPassengerFormId,
            type: 'toggle',
            label: 'Include Passenger',
            initialValue: true,
          },
        ],
      };
      const finalForm = [
        ...currentForm.slice(0, insertIndex),
        newPassengerForm,
        ...currentForm.slice(insertIndex),
      ];
      return {
        ...state,
        koalaQuoteForm: finalForm,
      };
    });
  },
}));

export const useSupportRequestStore = create<{
  //-----------------------------------------------//
  fetchFromProviderDatetime: string | undefined;
  //
  setFetchFromProviderDatetime: (dateTime: string | undefined) => void;

  //-----------------------------------------------//
  selectedRequestId: string | undefined;
  activeListIds: string[];
  activeListIdsSearched: string[];
  inactiveListIds: string[];
  inactiveListIdsSearched: string[];

  focus: SupportRequestFocus;
  setFocus: (focus: SupportRequestFocus) => void;

  isFromNew: boolean;
  setIsFromNew: (isNew: boolean) => void;

  activeMap: { [key: string]: string[] };
  inactiveMap: { [key: string]: string[] };
  requestMap: {
    [key: string]: (SupportRequestSocketRequest & { isActive: boolean }) | undefined;
  };
  groupMap: { [key: string]: SupportRequestSocketItem | undefined };
  searchTerm: string;
  setSocketResponse: (res: SupportRequestSocketResponse) => void;
  setSearchTerm: (term: string) => void;
  setSelectedRequestId: (id?: string) => void;

  clearStore: () => void;
}>((set) => ({
  // Initial state
  isFromNew: false,
  fetchFromProviderDatetime: undefined,
  selectedRequestId: undefined,
  focus: { unassignedBookings: false },
  // Actions to modify state
  setFetchFromProviderDatetime: (fetchFromProviderDatetime) =>
    set({ fetchFromProviderDatetime }),
  setSelectedRequestId: (id: string) => {
    set({ selectedRequestId: id });
  },
  setIsFromNew: (isNew: boolean) => {
    set({ isFromNew: isNew });
  },

  //-----------------------------------------------//
  activeListIds: [],
  activeListIdsSearched: [],
  inactiveListIds: [],
  inactiveListIdsSearched: [],
  activeMap: {},
  inactiveMap: {},
  requestMap: {},
  groupMap: {},
  searchTerm: '',

  setFocus: (focus: SupportRequestFocus) => {
    set((state) => {
      const { activeListIdsSearched, inactiveListIdsSearched } = filterLists(
        state,
        state.searchTerm,
        focus,
      );

      return {
        ...state,
        focus: focus,
        activeListIdsSearched,
        inactiveListIdsSearched,
      };
    });
  },

  setSocketResponse: (res: SupportRequestSocketResponse) =>
    set((state) => {
      const activeListIds: string[] = [];
      const inactiveListIds: string[] = [];
      const activeListIdsSearched: string[] = [];
      const inactiveListIdsSearched: string[] = [];

      const newActiveMap = {};
      const newInactiveMap = {};
      const newGroupMap = {};
      const newRequestMap = {};

      res.agent.forEach((groupItem) => {
        activeListIds.push(groupItem.id);
        if (
          groupItem.requests.some((req) =>
            isRequestValidForSearch({
              term: state.searchTerm,
              focus: state.focus,
              request: req,
              group: groupItem,
            }),
          )
        ) {
          activeListIdsSearched.push(groupItem.id);
        }

        newActiveMap[groupItem.id] = groupItem.requests.map((item) => item.id);
        newGroupMap[groupItem.id] = groupItem;

        groupItem.requests.forEach((req) => {
          newRequestMap[req.id] = { ...req, isActive: true };
        });
      });

      res.unassigned.forEach((groupItem) => {
        inactiveListIds.push(groupItem.id);
        if (
          groupItem.requests.some((req) =>
            isRequestValidForSearch({
              term: state.searchTerm,
              focus: state.focus,
              request: req,
              group: groupItem,
            }),
          )
        ) {
          inactiveListIdsSearched.push(groupItem.id);
        }

        newInactiveMap[groupItem.id] = groupItem.requests.map((item) => item.id);
        newGroupMap[groupItem.id] = groupItem;
        groupItem.requests.forEach((req) => {
          newRequestMap[req.id] = { ...req, isActive: false };
        });
      });

      return {
        ...state,
        activeListIds,
        inactiveListIds,
        activeListIdsSearched,
        inactiveListIdsSearched,
        activeMap: newActiveMap,
        inactiveMap: newInactiveMap,
        groupMap: newGroupMap,
        requestMap: newRequestMap,
      };
    }),

  setSearchTerm: (term: string) => {
    set((state) => {
      const { activeListIdsSearched, inactiveListIdsSearched } = filterLists(
        state,
        term,
        state.focus,
      );

      return {
        ...state,
        searchTerm: term,
        activeListIdsSearched,
        inactiveListIdsSearched,
      };
    });
  },
  clearStore: () => {
    set(() => {
      return {
        activeListIds: [],
        activeListIdsSearched: [],
        inactiveListIds: [],
        inactiveListIdsSearched: [],
        activeMap: {},
        inactiveMap: {},
        requestMap: {},
        groupMap: {},
        searchTerm: '',
      };
    });
  },
}));

function isRequestValidForSearch({
  term,
  focus,
  request,
  group,
}: {
  term: string;
  focus: SupportRequestFocus | undefined;
  request: SupportRequestSocketRequest | undefined;
  group: SupportRequestSocketItem | undefined;
}) {
  let searchValid = true;
  let focusValid = true;
  if (term) {
    const requestString = JSON.stringify(request).toLowerCase();
    const groupString = JSON.stringify(group).toLowerCase();

    const compareString = requestString + groupString;
    searchValid = compareString.includes(term.toLowerCase());
  }

  if (focus?.unassignedBookings) {
    const hasBooking = request?.dateActionBy || request?.dateRegarding;
    focusValid = !hasBooking;
  }

  return searchValid && focusValid;
}

const filterLists = (state: any, term: string, focus?: SupportRequestFocus) => {
  const activeListIdsSearched: string[] = [];
  const inactiveListIdsSearched: string[] = [];

  state.activeListIds.forEach((groupId) => {
    const requestIds = state.activeMap[groupId];
    if (!term && !Object.values(focus || {}).some((v) => !!v)) {
      activeListIdsSearched.push(groupId);
    } else if (
      requestIds.some((requestId) =>
        isRequestValidForSearch({
          term,
          focus,
          request: state.requestMap[requestId],
          group: state.groupMap[groupId],
        }),
      )
    ) {
      activeListIdsSearched.push(groupId);
    }
  });

  state.inactiveListIds.forEach((groupId) => {
    const requestIds = state.inactiveMap[groupId];
    if (!term && !Object.values(focus || {}).some((v) => !!v)) {
      inactiveListIdsSearched.push(groupId);
    } else if (
      requestIds.some((requestId) =>
        isRequestValidForSearch({
          term,
          focus,
          request: state.requestMap[requestId],
          group: state.groupMap[groupId],
        }),
      )
    ) {
      inactiveListIdsSearched.push(groupId);
    }
  });

  return {
    activeListIdsSearched,
    inactiveListIdsSearched,
  };
};

function mapBookingToFormEntries(
  booking: BookingClientWithPurchasesAndTickets,
): KoalaQuoteFormData {
  const passengerTickets = booking.purchases
    .map((purchase) =>
      purchase.tickets
        .filter((ticket) => ticket.ticketType === 'Passenger')
        .map((ticket) => ({
          firstName: ticket.p_firstName,
          lastName: ticket.p_lastName,
          nationality: ticket.p_nationality,
          dateOfBirth: ticket.p_birthDate,
          id: ticket._id,
        })),
    )
    .flat()
    .filter(
      onlyUniqueByFunc(
        (ticket) => ticket.firstName + ticket.lastName + ticket.dateOfBirth,
      ),
    );
  return {
    currency: booking.paymentSessions[0].paymentSummary.currency ?? '',
    passengers: passengerTickets,
    tripDetails: booking.purchases.map((purchase) => ({
      id: purchase._id,
      origin: purchase.origin,
      destination: purchase.destination,
      companyId: purchase.companyId,
      vesselId: purchase.vesselId,
      departureDate: purchase.departureDate,
      departureTime: purchase.departureTime,
      arrivalDate: dayjs(purchase.arrivalDateTime).format('YYYY-MM-DD'),
      arrivalTime: purchase.arrivalTime,
    })),
  };
}
